import { Box, Spinner } from 'native-base';
import React, { useMemo, useState } from 'react';
import { LayoutChangeEvent, Platform } from 'react-native';

import Carousel from 'app/components/Carousel';
import ResponsiveContainer from 'app/components/ResponsiveContainer';
import ReservationTicket from 'app/components/TicketCarousel/ReservationTicket';
import Ticket from 'app/components/TicketCarousel/Ticket';
import TicketsCarouselPagination from 'app/components/TicketCarousel/TicketCarouselPagination';
import useAttraction from 'app/hooks/useAttraction';
import useConfiguration from 'app/hooks/useConfiguration';
import useReservationTextTranslationHelper from 'app/hooks/useReservationTextTranslationHelper';
import useWindow from 'app/hooks/useWindow';
import { getPassCountByClass } from 'app/services/EntitlementHelper';
import {
  EntitlementReservation,
  Pass,
  TicketCategory,
  TicketClass,
} from 'app/services/GuestCenterService.types';
import { createReservationTicket, createTicket } from 'app/services/TicketHelper';
import { MAX_WIDTH_WEB } from 'app/theme/breakpoints';

export type TicketCarouselProps = {
  passes: Pass[];
  reservation?: EntitlementReservation;
  orderNumber?: string;
  ticketCategory?: TicketCategory;
};

export default function TicketCarousel({
  passes,
  reservation,
  orderNumber,
  ticketCategory,
}: TicketCarouselProps) {
  const { rawWidth } = useWindow();
  const { data: productLineups = [] } = useConfiguration({
    select: (res) => res.productLineups.configuration,
  });
  const [maxTicketHeight, setMaxTicketHeight] = useState(0);

  const productContentKey =
    productLineups.find((pl) => pl.productLineupKey === passes[0]?.productLineupKey)?.contentKey ??
    '';
  const attractionContentKey = reservation?.attractionConfig.contentKey ?? '';
  const { attractionContent, productContent } = useAttraction({
    productContentKey,
    attractionContentKey,
  });
  const rt = useReservationTextTranslationHelper({
    product: productContent,
    attraction: attractionContent,
  });

  const showOrderBarcode = reservation?.attractionConfig.ticketingSystem?.showOrderBarcode;
  const tickets = showOrderBarcode ? passes.slice(0, 1) : passes;
  const buttonWrapperStyle = useMemo(() => {
    if (Platform.OS !== 'web') return undefined;

    const controlsWidth = 80;

    // beside tickets
    if (rawWidth > MAX_WIDTH_WEB + controlsWidth) {
      return {
        marginLeft: -(controlsWidth / 2),
        width: MAX_WIDTH_WEB + controlsWidth,
      };
    }

    // below tickets
    return { top: maxTicketHeight ? maxTicketHeight / 2 : undefined };
  }, [maxTicketHeight, rawWidth]);

  const handleTicketLayout = (e: LayoutChangeEvent) => {
    const height = Math.ceil(e.nativeEvent.layout.height);
    if (height > maxTicketHeight) setMaxTicketHeight(height);
  };

  if (!productContent || (reservation && !attractionContent)) {
    return <Spinner testID="TicketCarouselLoading" />;
  }

  return (
    <Box testID="TicketCarousel">
      <ResponsiveContainer maxWidth={600} padded={false} w="full">
        <Carousel
          buttonWrapperStyle={buttonWrapperStyle}
          loop={false}
          removeClippedSubviews={false}
          renderPagination={(index, total) => (
            <TicketsCarouselPagination cardHeight={maxTicketHeight} index={index} total={total} />
          )}
          showsButtons={Platform.OS === 'web'}
          style={{ height: maxTicketHeight || undefined }}
        >
          {tickets.map((pass) => {
            const ticketProps = {
              key: pass.barcode,
              opacity: maxTicketHeight ? 1 : 0,
              px: '3',
              py: '2',
              w: 'full',
              onLayout: handleTicketLayout,
              ...createTicket({
                pass,
                productContent,
                attractionKey: reservation?.attractionKey ?? '',
                orderNumber,
                ticketCategory,
              }),
            };

            if (reservation && attractionContent && showOrderBarcode) {
              return (
                <ReservationTicket
                  {...ticketProps}
                  barcodeDisplay={reservation?.display ?? ''}
                  barcodeScan={reservation?.scan ?? ''}
                  guestName={`Adult (${getPassCountByClass(
                    passes,
                    TicketClass.Adult
                  )}) Child (${getPassCountByClass(passes, TicketClass.Child)})`}
                  hideAge
                  passBarcode={pass.barcode}
                  reservation={reservation}
                  reservationDisplay={{
                    ...createReservationTicket({
                      attractionContent,
                      reservation,
                      rt,
                      ticketCategory,
                    }),
                    hideReferenceNumber: true,
                  }}
                  showAddToWallet={!showOrderBarcode}
                />
              );
            }
            if (reservation && reservation.barcodes.includes(pass.barcode) && attractionContent) {
              return (
                <ReservationTicket
                  {...ticketProps}
                  passBarcode={pass.barcode}
                  reservation={reservation}
                  reservationDisplay={createReservationTicket({
                    attractionContent,
                    reservation,
                    rt,
                    ticketCategory,
                  })}
                  showAddToWallet
                />
              );
            }
            return <Ticket {...ticketProps} />;
          })}
        </Carousel>
      </ResponsiveContainer>
      {/* <Dump data={{ passes, reservation }} /> */}
    </Box>
  );
}
